// ============================================================
// THE COMPANY — App Shell
// ============================================================

import { Component, useEffect, useRef, useState, type ReactNode } from "react";
import { useCompanyStore } from "./store/useCompanyStore";
import AuthGate from "./components/AuthGate";
import Dashboard from "./components/Dashboard";

class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            backgroundColor: "#0a0a0f",
            color: "#f0f0f5",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "system-ui",
            padding: "2rem",
          }}
        >
          <div style={{ maxWidth: 600 }}>
            <h1 style={{ color: "#ef4444", marginBottom: "1rem" }}>
              Something went wrong
            </h1>
            <pre
              style={{
                backgroundColor: "#111118",
                padding: "1rem",
                borderRadius: 8,
                fontSize: "0.875rem",
                overflow: "auto",
                whiteSpace: "pre-wrap",
                border: "1px solid #2a2a3a",
              }}
            >
              {this.state.error.message}
              {"\n\n"}
              {this.state.error.stack}
            </pre>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                marginTop: "1rem",
                padding: "0.5rem 1rem",
                backgroundColor: "#6366f1",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppInner />
    </ErrorBoundary>
  );
}

function AppInner() {
  const isAuthenticated = useCompanyStore((s) => s.isAuthenticated);
  const fetchAll = useCompanyStore((s) => s.fetchAll);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || hasFetched.current) return;
    hasFetched.current = true;

    setLoading(true);
    fetchAll()
      .catch((err) =>
        setLoadError(err instanceof Error ? err.message : "Failed to load"),
      )
      .finally(() => setLoading(false));
  }, [isAuthenticated, fetchAll]);

  // Not authenticated — show the password gate
  if (!isAuthenticated) {
    return <AuthGate />;
  }

  // Error loading data
  if (loadError) {
    return (
      <div
        className="flex h-screen w-screen items-center justify-center"
        style={{ backgroundColor: "var(--bg-base)" }}
      >
        <div
          className="flex flex-col items-center gap-4"
          style={{ maxWidth: 400 }}
        >
          <h2 className="text-lg font-semibold" style={{ color: "#ef4444" }}>
            Failed to load
          </h2>
          <p
            className="text-sm text-center"
            style={{ color: "var(--text-secondary)" }}
          >
            {loadError}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md px-4 py-2 text-sm font-medium text-white"
            style={{ backgroundColor: "var(--accent)" }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  // Authenticated but still loading initial data
  if (loading) {
    return (
      <div
        className="flex h-screen w-screen items-center justify-center"
        style={{ backgroundColor: "var(--bg-base)" }}
      >
        <div className="flex flex-col items-center gap-4">
          <div
            className="h-8 w-8 animate-spin rounded-full border-2 border-t-transparent"
            style={{
              borderColor: "var(--accent)",
              borderTopColor: "transparent",
            }}
          />
          <span
            className="text-sm tracking-wide"
            style={{ color: "var(--text-muted)" }}
          >
            Loading company data...
          </span>
        </div>
      </div>
    );
  }

  // Authenticated and data loaded — show the dashboard
  return <Dashboard />;
}
